// Silo Cube — master on/off preference (shared across all components).
//
// Mirrors the dark-mode pattern but uses a tiny module-level pub/sub so EVERY
// useSiloCubeEnabled() instance (the toggle in the header AND the dashboard
// cards) stays in sync the instant the switch is flipped — no reload needed.
// Persisted to localStorage for instant application and mirrored to the server
// so the setting follows the user across devices. Default is ON (opt-OUT).
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const LS_KEY = "mysilo_silo_cube";

function readLocalCube() {
  try {
    const v = window.localStorage.getItem(LS_KEY);
    return v === null ? true : v === "1";
  } catch {
    return true;
  }
}

function writeLocalCube(enabled) {
  try {
    window.localStorage.setItem(LS_KEY, enabled ? "1" : "0");
  } catch {
    // ignore (private browsing)
  }
}

let _value = readLocalCube();
let _synced = false;
const _subs = new Set();

function _setAll(v) {
  _value = v;
  writeLocalCube(v);
  _subs.forEach((fn) => fn(v));
}

// Jul 2026 — let the Silo Art V2 mode control drive the legacy V1 on/off flag
// live (off ⇒ false, v1/v2 ⇒ true) so every cube instance re-syncs instantly.
export function setSiloCubeEnabled(v) {
  _setAll(!!v);
}

export function useSiloCubeEnabled() {
  const [enabled, setEnabled] = useState(_value);

  useEffect(() => {
    const fn = (v) => setEnabled(v);
    _subs.add(fn);
    setEnabled(_value);
    return () => _subs.delete(fn);
  }, []);

  // one-time server sync (first mount across the whole app)
  useEffect(() => {
    if (_synced) return;
    _synced = true;
    (async () => {
      try {
        const r = await api.get("/me/silo-cube");
        const remote = !!r.data?.enabled;
        if (remote !== _value) _setAll(remote);
      } catch {
        _synced = false; // allow a retry on next mount if it failed
      }
    })();
  }, []);

  const toggle = async () => {
    const next = !_value;
    _setAll(next);
    try {
      await api.patch("/me/silo-cube", { enabled: next });
    } catch {
      // silent — local already applied
    }
  };

  return { enabled, toggle };
}
